import { useState } from "react";
import { User, Search, X, Check, Loader2 } from "lucide-react";
import {
  useSearchMembersQuery,
  MemberSearchResult,
} from "../api/committeesApi";

function fullName(m: MemberSearchResult) {
  return [m.surname, m.firstname, m.othernames].filter(Boolean).join(" ");
}

interface MemberPickerProps {
  label: string;
  valueUserId: number | null;
  onPickUserId: (userId: number | null) => void;
  onPick?: (member: MemberSearchResult | null) => void; // Added for flexibility
  disabled?: boolean;
}

export default function MemberPicker({
  label,
  valueUserId,
  onPickUserId,
  onPick,
  disabled,
}: MemberPickerProps) {
  const [q, setQ] = useState("");
  const [selectedName, setSelectedName] = useState<string | null>(null);

  const { data = [], isFetching } = useSearchMembersQuery(q, {
    skip: q.trim().length < 2,
  });

  const handlePick = (m: MemberSearchResult) => {
    setSelectedName(fullName(m));
    onPickUserId(typeof m.user_id === "number" ? m.user_id : null);
    if (onPick) onPick(m);
    setQ("");
  };

  const handleClear = () => {
    setSelectedName(null);
    onPickUserId(null);
    if (onPick) onPick(null);
  };

  return (
    <div className="relative space-y-1">
      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
        {label}
      </label>

      <div className="relative group">
        {!selectedName ? (
          <>
            <div className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-500 transition-colors">
              <Search className="w-3.5 h-3.5" />
            </div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              disabled={disabled}
              placeholder="Search by name or email..."
              className="w-full pl-6 border-b-2 border-gray-100 py-2 text-sm font-bold text-gray-800 outline-none focus:border-indigo-500 transition-all placeholder:text-gray-300 placeholder:font-normal disabled:opacity-30 bg-transparent"
            />
          </>
        ) : (
          <div className="flex items-center justify-between border-b-2 border-indigo-100 py-2 bg-indigo-50/30 px-2 rounded-t-lg transition-all">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-[10px] text-white font-bold">
                {selectedName?.charAt(0) || "U"}
              </div>
              <div>
                <div className="text-sm font-black text-gray-900 leading-none">
                  {selectedName || `User #${valueUserId}`}
                </div>
                <div className="text-[10px] text-indigo-500 font-bold uppercase tracking-widest mt-0.5">
                  Selected Member
                </div>
              </div>
            </div>
            {!disabled && (
              <button
                onClick={handleClear}
                className="p-1 hover:bg-white rounded-full text-gray-400 hover:text-red-500 transition-all shadow-sm"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>

      {q.trim().length >= 2 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-100 rounded-xl shadow-xl shadow-indigo-100/50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
          {isFetching ? (
            <div className="p-4 flex items-center justify-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
              <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
              Searching Database...
            </div>
          ) : data.length ? (
            <div className="py-2">
              {data.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => handlePick(m)}
                  className="w-full text-left px-4 py-3 hover:bg-indigo-50 flex items-center justify-between group transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-50 group-hover:bg-white flex items-center justify-center text-gray-400 group-hover:text-indigo-600 transition-all border border-transparent group-hover:border-indigo-100">
                      <User className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-black text-gray-800 group-hover:text-indigo-700 transition-colors">
                        {fullName(m)}
                      </div>
                      <div className="text-[10px] font-bold text-gray-400 group-hover:text-indigo-400 uppercase tracking-tight">
                        {m.membership_no} • {m.email}
                      </div>
                    </div>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <Check className="w-4 h-4 text-indigo-600" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="text-xs font-black text-gray-300 uppercase tracking-[0.2em]">
                No Members Found
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
